import { Component, computed, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SidebarService } from '@services/sidebar/sidebar.service';
import { TranslateModule } from '@ngx-translate/core';
import { MixpanelService } from '@services/mixpanel/mixpanel.service';
import { AuthService } from '@services/auth/auth.service';
import { RouterLink } from '@angular/router';
import { FeatureService } from '@services/feature-toggle/feature.service';
import { AuthenticatedUser, DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { HelperService } from '@services/helper/helper.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { AwTooltipComponent } from '@components/misc/aw-tooltip/aw-tooltip.component';
import { FormsModule } from '@angular/forms';
import { OwnerSelectorComponent } from './owner-selector/owner-selector.component';
import { SamlService } from '@services/auth/saml.service';
import { environment } from 'environment';
import { TrialService } from '@services/trial/trial.service';
import { ToastService } from '@services/helper/toast.service';

@Component({
    selector: 'app-sidebar',
    imports: [TranslateModule, RouterLink, AwTooltipComponent, FormsModule, OwnerSelectorComponent],
    templateUrl: './sidebar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {
    dashboardOwnerUser: DashboardOwnerUser = this.helperService.getDashboardOwnerUser();
    authenticatedUser: AuthenticatedUser = this.localStorageService.getItem('authenticatedUser') as AuthenticatedUser;

    isDemo: boolean = environment.isDemo;

    isActionsEnabled = this.featureService.getFeatureAccess('actions', 'route_access');
    isOperatorDevicesEnabled = this.featureService.getFeatureAccess('operator_devices', 'route_access');
    isOperatorHealthMonitorEnabled = this.featureService.getFeatureAccess('operator_health_monitor', 'route_access');
    isNoticeBoardsEnabled = this.featureService.getFeatureAccess('notice_boards', 'route_access');

    isTrialGranted = (module: string) =>
        computed(() => {
            if (!this.trialService.isTrialActive()) return false;
            const full = this.featureService.accessMap();
            const base = this.featureService.baseAccessMap();
            const fullModule = full?.[module];
            if (!fullModule) return false;
            const baseModule = base?.[module];
            if (!baseModule) return true; // Entirely new module from trial
            return Object.keys(fullModule).some(key => {
                const fv = fullModule[key];
                const bv = baseModule[key];
                if (bv === undefined) return true; // New feature key
                if (typeof fv === 'number') return fv > (bv as number); // Higher limit
                return fv !== bv; // Changed value (e.g. false → true)
            });
        });

    constructor(
        protected sidebarService: SidebarService,
        private mixpanelService: MixpanelService,
        protected authService: AuthService,
        protected featureService: FeatureService,
        private helperService: HelperService,
        protected samlService: SamlService,
        private localStorageService: LocalStorageService,
        protected trialService: TrialService,
        private toastService: ToastService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.samlService.setupSamlService();
    }

    trialLoading = false;

    async startTrial(): Promise<void> {
        this.trialLoading = true;
        await this.trialService.activateTrial();
        this.trialLoading = false;
        this.sidebarService.disableSidebar();
        this.toastService.success('upgrade.trial_activated_toast');
    }

    signOut() {
        this.mixpanelService.track('Action: Log Out Click', { action: 'log out was clicked' });
        this.authService.signOut();
    }
}
